import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Card } from '../card/card';
import { ZagIcon } from '../icon/icon';
import { WebhookListRowData } from '../webhook-list-row/webhook-list-row';

/**
 * Re-export WebhookListRowData as WebhookCardData for consistency
 */
export type WebhookCardData = WebhookListRowData;

/**
 * WebhookCard Component
 *
 * Displays a webhook endpoint's information in a card format.
 * Uses the Card component for consistent styling.
 *
 * @example
 * ```html
 * <zag-webhook-card
 *   [webhook]="webhookData"
 *   [editRoute]="['/settings/webhooks', webhookData.id]"
 *   (cardClick)="onWebhookSelected($event)">
 * </zag-webhook-card>
 * ```
 */
@Component({
  selector: 'zag-webhook-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Card, ZagIcon],
  host: {
    '[class.zag-webhook-card]': 'true',
  },
  styleUrl: './webhook-card.scss',
  template: `
    <zag-card variant="interactive" (cardClick)="onCardClick()">
      <!-- Header: Icon, Name and Status -->
      <div card-header class="zag-webhook-card__header">
        <span zagIcon="webhook" class="zag-webhook-card__icon"></span>
        <div class="zag-webhook-card__text">
          <h3 class="zag-card-title">{{ webhook().name }}</h3>
          <span
            class="zag-webhook-card__status"
            [class.zag-webhook-card__status--active]="webhook().isActive"
            [class.zag-webhook-card__status--inactive]="!webhook().isActive"
          >
            {{ webhook().isActive ? activeLabel() : inactiveLabel() }}
          </span>
        </div>
      </div>

      <!-- Body: URL and event type chips -->
      <p class="zag-webhook-card__url">{{ truncateUrl(webhook().url) }}</p>

      <div class="zag-webhook-card__event-types">
        @for (eventType of webhook().eventTypes.slice(0, 3); track eventType) {
          <span class="zag-webhook-card__event-chip">{{ eventType }}</span>
        }
        @if (webhook().eventTypes.length > 3) {
          <span class="zag-webhook-card__event-chip zag-webhook-card__event-chip--more">+{{ webhook().eventTypes.length - 3 }}</span>
        }
      </div>
    </zag-card>
  `,
})
export class WebhookCard {
  private readonly router = inject(Router);

  /** The webhook data to display */
  readonly webhook = input.required<WebhookCardData>();

  /** Route for navigation (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Translated label for active status */
  readonly activeLabel = input('');

  /** Translated label for inactive status */
  readonly inactiveLabel = input('');

  /** Emitted when the card is clicked */
  readonly cardClick = output<WebhookCardData>();

  /** Truncate long URLs for display */
  truncateUrl(url: string): string {
    if (url.length <= 50) return url;
    return url.slice(0, 47) + '…';
  }

  /** Handle card click - navigates to editRoute if set, then emits cardClick */
  onCardClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.cardClick.emit(this.webhook());
  }
}
